import React, { useEffect, useState } from "react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { can_visit, getPractice, notify } from "../../helpers";
import { BriefcaseMedical, CloudUpload, DatabaseBackup, FilePlus, FileUp, Loader, UserRoundPlus } from "lucide-react";
import { syncIcdCptData, syncPatientData } from "../../services/workerServices";
import Spacing from "../Spacing";
import Card from "../Card";
import Select from "react-select";
import ConfirmationFileUploadModal from "../Modal/ConfirmationFileUploadModal";

export default function PracticeDataSync({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  let location_id = getPractice("location_id");
  let file_format = getPractice("emr_type");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    account_id = selectedPracticeChange?.account_id;
    location_id = selectedPracticeChange?.location_id;
    file_format = selectedPracticeChange?.emr_type;
  }

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    file_format: ""
  };
  const [formData, setFormData] = useState(defaultValues);
  const [uploadPatientDisabled, setUploadPatientDisabled] = useState(true);
  const [uploadPatientLoader, setUploadPatientLoader] = useState(false);
  const [uploadIcdCptDisabled, setUploadIcdCptDisabled] = useState(true);
  const [uploadIcdCptLoader, setUploadIcdCptLoader] = useState(false);
  const [patientFiles, setPatientFiles] = useState([]);
  const [filePatientError, setPatientFileError] = useState(false);
  const [icdCptFiles, setIcdCptFiles] = useState([]);
  const [fileIcdCptError, setIcdCptFileError] = useState(false);
  const [patientFileUploadConfirmation, setPatientFileUploadConfirmation] = useState(false);
  const [icdCptFileUploadConfirmation, setIcdCptFileUploadConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [requiredColumns, setRequiredColumns] = useState(null);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const [patientIdSyncStatus, setPatientIdSyncStatus] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');

  const [emrType] = useState([
    { value: "ECW", label: "ECW" },
    { value: "MDLand", label: "MDLand" },
    { value: "VeradigmEHR", label: "VeradigmEHR" },
    { value: 'AthenaHealth', label: 'AthenaHealth', }
  ]);

  useEffect(() => {
    setFormData({
      ...formData,
      location_id: location_id,
      account_id: account_id,
      file_format: file_format || "MDLand",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const validTypes = new Set([
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
    "application/csv",
  ]);

  const filterValidFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    const invalid = arr.some((f) => !validTypes.has(f.type));
    return { files: invalid ? [] : arr, invalid };
  };

  //Patients: file changes & DnD
  const handlePatientFileChange = (e) => {
    const { files, invalid } = filterValidFiles(e.target.files);
    setPatientFileError(invalid || files.length === 0);
    setPatientFiles(files);
    setUploadPatientDisabled(!formData.file_format || files.length === 0);
  };

  const handlePatientDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragover");
    const { files, invalid } = filterValidFiles(e.dataTransfer.files);
    setPatientFileError(invalid || files.length === 0);
    setPatientFiles(files);
    setUploadPatientDisabled(!formData.file_format || files.length === 0);
  };

  // ICD/CPT: file changes & DnD
  const handleICDCPTFileChange = (e) => {
    const { files, invalid } = filterValidFiles(e.target.files);
    setIcdCptFileError(invalid || files.length === 0);
    setIcdCptFiles(files);
    setUploadIcdCptDisabled(!formData.file_format || files.length === 0);
  };

  const handleIcdDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragover");
    const { files, invalid } = filterValidFiles(e.dataTransfer.files);
    setIcdCptFileError(invalid || files.length === 0);
    setIcdCptFiles(files);
    setUploadIcdCptDisabled(!formData.file_format || files.length === 0);
  };

  const fileListLabel = (filesArr) => {
    if (!filesArr?.length) return "";
    if (filesArr.length === 1) return filesArr[0].name;
    return filesArr.map((f) => f.name).join(", ");
  };

  const requiredColumnsList = (columns = []) => {
    if (!columns || columns.length === 0) return "";
    return columns.join(", ");
  };

  const handelShowWarning = (e) => {
    e.preventDefault();
    setPatientFileUploadConfirmation(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const handelShowIcdWarning = (e) => {
    e.preventDefault();
    setIcdCptFileUploadConfirmation(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const handlePatientIdUploadCheck = (e) => {
    setPatientIdSyncStatus(!!e.target.checked);
  };

  const handelSubmitPatientData = async (e) => {
    if (!patientFiles?.length) {
      notify("error", `Please upload an patient file(s)`, 5);
      setPatientFileUploadConfirmation(false);
      window.scrollTo(0, previousScrollPosition);
      return;
    }

    setUploadPatientDisabled(true);
    setUploadPatientLoader(true);

    try {
      const total = patientFiles.length;
      for (let i = 0; i < total; i++) {
        const file = patientFiles[i];

        const updatedData = {
          ...formData,
          file,
          sync_patient_id_only: patientIdSyncStatus,
        };
        setSelectedFileName(file?.name);
        const response = await syncPatientData(updatedData);

        if (response?.detail) {
          setErrorMessage(`❌ ${file?.name}: ${response?.detail}`);
          notify("error", `❌ ${file?.name}: ${response?.detail}`, 5);
          setUploadPatientDisabled(false);
        } else {
          if(response?.data?.status === 'success'){
            notify("success", `✅ ${file?.name}: ${response?.data?.message}`, 5);
            if(total === i + 1) {
              setPatientFiles([]);
              setUploadPatientDisabled(true);
              setPatientFileUploadConfirmation(false);
              setErrorMessage(null);
              setRequiredColumns(null);
              window.scrollTo(0, previousScrollPosition);
            }
          } else {
            setErrorMessage(response?.data?.message);
            setRequiredColumns(response?.data?.required_columns || null);
            if(total !== i + 1) {
              notify("error", `❌ ${file?.name}: ${response?.data?.message}`, 5);
            }
            setUploadPatientDisabled(false);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        setErrorMessage(error?.response?.data?.detail);
      }
      setUploadPatientDisabled(false);
    } finally {
      setSelectedFileName('');
      setUploadPatientLoader(false);
    }
  };

  const handelSubmitIcdCptData = async (e) => {
    if (!icdCptFiles?.length) {
      notify("error", `Please upload an ICD & CPT file(s)`, 5);
      setIcdCptFileUploadConfirmation(false);
      window.scrollTo(0, previousScrollPosition);
      return;
    }

    setUploadIcdCptDisabled(true);
    setUploadIcdCptLoader(true);

    try {
      const total = icdCptFiles.length;
      for (let i = 0; i < total; i++) {
        const file = icdCptFiles[i];
        const updatedData = {
          ...formData,
          file
        };
        setSelectedFileName(file?.name);
        const response = await syncIcdCptData(updatedData);
        
        if (response?.detail) {
          setErrorMessage(`❌ ${file?.name}: ${response?.detail}`);
          notify("error", `❌ ${file?.name}: ${response?.detail}`, 5);
          setUploadIcdCptDisabled(false);
        } else {
          if(response?.data?.status === 'success'){
            notify("success", `✅ ${file?.name}: ${response?.data?.message}`, 5);
            if(total === i + 1) {
              setIcdCptFiles([]);
              setUploadIcdCptDisabled(true);
              setIcdCptFileUploadConfirmation(false);
              setErrorMessage(null);
              setRequiredColumns(null);
              window.scrollTo(0, previousScrollPosition);
            }
          } else {
            setErrorMessage(response?.data?.message);
            setRequiredColumns(response?.data?.required_columns || null);
            if(total !== i + 1) {
              notify("error", `❌ ${file?.name}: ${response?.data?.message}`, 5);
            }
            setUploadIcdCptDisabled(false);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        setErrorMessage(error?.response?.data?.detail);
      }
      setUploadIcdCptDisabled(false);
    } finally {
      setSelectedFileName('');
      setUploadIcdCptLoader(false);
    }
  };

  useDocumentTitle("Practice Data Sync");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: '#0b68cb' }}
          >
            <FileUp width={18} height={18} />
          </i>
          Practice Data Sync
        </h2>
      </div>
      <Spacing lg="6" sm="6" />

      <div className="pl-2">
        <strong className="docpro_red_color">Important Note: </strong>
        <ul className="pt-1" style={{ listStyle: 'auto', paddingLeft: '30px' }}>
          <li>First, upload the patient data, then upload the ICD/CPT data.</li>
          <li>Your default location is <span className="docpro_accent_color">{getPractice("location_name")}</span>. If you want to change it, select a different location from the dropdown.</li>
          <li>Any blank patient records will be uploaded to the default location.</li>
        </ul>
      </div>
      
      <div className="docpro_sync_data_section">
        <Card variant="docpro_card w-full">
          <h3 className="docpro_fs_16 docpro_mb_0 docpro_fw_700 docpro_secondary_color flex items-center docpro_grid_gap_4">
            <UserRoundPlus width={22} height={22} />
            Patients Data
          </h3>
          <Spacing lg="16" sm="16" />

          <form onSubmit={handelShowWarning} className="docpro_text_start">
            <div className="docpro_badge_green docpro_sync_data_badge">
              <span className="icon">
                <BriefcaseMedical width={24} height={24} />
              </span>
              <div>
                <p className="mb-0"><span style={{ fontWeight: 600 }}>Account Name :</span> {getPractice("account_name")}</p>
                <p className="mb-0"><span style={{ fontWeight: 600 }}>Location Name :</span> {getPractice("location_name")} <span className="docpro_badge docpro_badge_default">Default</span></p>
              </div>
            </div>

            <Spacing lg="20" sm="20" />
            <div className="card_body">
              <label className="docpro_fw_500 docpro_primary_color">
                File Format
              </label>
              <Select
                name="file_format"
                options={emrType}
                onChange={(selectedOption) => {
                  setFormData({ ...formData, file_format: selectedOption.value });
                  setUploadPatientDisabled(false);
                }}
                placeholder="Search..."
                isSearchable={true}
                value={emrType.find(
                  (option) => option.value === formData.file_format
                )}
                className="react-select-container w-full"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '43px',
                    borderRadius: '8px',
                    borderColor: 'var(--border)',
                  }),
                }}
              />

              <Spacing lg="20" sm="20" />
              <label className="docpro_primary_color">
                <span className="docpro_fw_500">Patient File(s)</span> <small className="docpro_red_color">(.xlsx, .xls, .csv)</small>
              </label>
              <div
                className="docpro_input_file_container"
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
                onDrop={handlePatientDrop}
                role="button"
                tabIndex={0}
                aria-label="Upload Excel/CSV files by dragging or clicking"
              >
                <span className="docpro_file_icon" aria-hidden="true">
                  <CloudUpload height={28} width={28} />
                </span>
                <div className="docpro_file_text">
                  <div className="docpro_file_title">
                    {patientFiles.length ? fileListLabel(patientFiles) : "Drag and drop patient file(s)"}
                  </div>
                  <div className="docpro_file_hint">
                    {patientFiles.length ? '' : 'here, or click to upload'}
                  </div>
                </div>

                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="docpro_file_hidden_input"
                  onChange={handlePatientFileChange}
                  multiple
                />
              </div>
              {filePatientError && (
                <p className="docpro_error_message"> Please upload a valid Excel/CSV file(s). </p>
              )}
            </div>

            {can_visit("administrator") && (
              <>
                <Spacing lg="10" sm="10" />
                <div className="docpro_checkbox" >
                  <input
                    type="checkbox"
                    value={patientIdSyncStatus}
                    name="sync_patient_id_only" 
                    onChange={handlePatientIdUploadCheck}
                  />
                  <label style={{ marginLeft: "8px" }}> Sync EMR patient id only not upload patient information</label>
                </div>
              </>
            )}

            <Spacing lg="25" sm="25" />
            <button type="submit"
              className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg w-full"
              style={{ padding: '12px 13px' }}
              disabled={uploadPatientDisabled}
            >
              <DatabaseBackup width={20} height={20} />
              <span>Sync Patient</span>
              {uploadPatientLoader && (
                <span className="docpro_loading">
                  <Loader width={20} height={20} />
                </span>
              )}
            </button>
            <Spacing lg="3" sm="3" />
          </form>
        </Card>

        <div className="docpro_data_sync_arrow"></div>

        <Card variant="docpro_card w-full">
          <h3 className="docpro_fs_16 docpro_mb_0 docpro_fw_700 docpro_secondary_color flex items-center docpro_grid_gap_4">
            <FilePlus width={22} height={22} />
            ICD & CPT Data
          </h3>
          <Spacing lg="16" sm="16" />

          <form onSubmit={handelShowIcdWarning} className="docpro_text_start">
            <div className="docpro_badge_green docpro_sync_data_badge">
              <span className="icon">
                <BriefcaseMedical width={24} height={24} />
              </span>
              <div>
                <p className="mb-0"><span style={{ fontWeight: 600 }}>Account Name :</span> {getPractice("account_name")}</p>
                <p className="mb-0"><span style={{ fontWeight: 600 }}>Location Name :</span> {getPractice("location_name")} <span className="docpro_badge docpro_badge_default">Default</span></p>
              </div>
            </div>

            <Spacing lg="20" sm="20" />
            <div className="card_body">
              <label className="docpro_fw_500 docpro_primary_color">
                File Format
              </label>
              <Select
                name="file_format"
                options={emrType}
                onChange={(selectedOption) => {
                  setFormData({ ...formData, file_format: selectedOption.value });
                  setUploadPatientDisabled(false);
                }}
                placeholder="Search..."
                isSearchable={true}
                value={emrType.find(
                  (option) => option.value === formData.file_format
                )}
                className="react-select-container w-full"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '43px',
                    borderRadius: '8px',
                    borderColor: 'var(--border)',
                  }),
                }}
              />

              <Spacing lg="20" sm="20" />
              <label className="docpro_primary_color">
                <span className="docpro_fw_500">ICD, CPT File(s)</span> <small className="docpro_red_color">(.xlsx, .xls, .csv)</small>
              </label>
              <div
                className="docpro_input_file_container"
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
                onDrop={handleIcdDrop}
                role="button"
                tabIndex={0}
                aria-label="Upload Excel/CSV files by dragging or clicking"
              >
                <span className="docpro_file_icon" aria-hidden="true">
                  <CloudUpload height={28} width={28} />
                </span>
                <div className="docpro_file_text">
                  <div className="docpro_file_title">
                    {icdCptFiles.length ? fileListLabel(icdCptFiles) : 'Drag and drop ICD, CPT file(s)'}
                  </div>
                  <div className="docpro_file_hint">
                    {icdCptFiles.length ? '' : 'here, or click to upload'}
                  </div>
                </div>

                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="docpro_file_hidden_input"
                  onChange={handleICDCPTFileChange}
                  multiple
                />
              </div>
              {fileIcdCptError && (
                <p className="docpro_error_message"> Please upload a valid Excel/CSV file(s). </p>
              )}
            </div>

            {can_visit("administrator") && (
              <Spacing lg="30" sm="30" />
            )}
            <Spacing lg="25" sm="25" />
            <button type="submit"
              className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg w-full"
              style={{ padding: '12px 13px' }}
              disabled={uploadIcdCptDisabled}
            >
              <DatabaseBackup width={20} height={20} />
              <span>Sync ICD & CPT</span>
              {uploadIcdCptLoader && (
                <span className="docpro_loading">
                  <Loader width={20} height={20} />
                </span>
              )}
            </button>
            <Spacing lg="3" sm="3" />
          </form>
        </Card>
      </div>

      {patientFileUploadConfirmation && (
        <ConfirmationFileUploadModal 
          openModal={patientFileUploadConfirmation}
          setOpenModal={setPatientFileUploadConfirmation}
          previousScrollPosition={previousScrollPosition}
          uploadDisabled={uploadPatientDisabled}
          uploadLoader={uploadPatientLoader}
          modalAction={handelSubmitPatientData}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          selectedFileName={selectedFileName}
          requiredColumns={requiredColumnsList(requiredColumns)}
        />
      )}

      {icdCptFileUploadConfirmation && (
        <ConfirmationFileUploadModal 
          openModal={icdCptFileUploadConfirmation}
          setOpenModal={setIcdCptFileUploadConfirmation}
          previousScrollPosition={previousScrollPosition}
          uploadDisabled={uploadIcdCptDisabled}
          uploadLoader={uploadIcdCptLoader}
          modalAction={handelSubmitIcdCptData}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          fileName={icdCptFiles}
          selectedFileName={selectedFileName}
          requiredColumns={requiredColumnsList(requiredColumns)}
        />
      )}
    </>
  );
}
